import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Alert, Button, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { rateCoaching } from '../../service'
import { RATING_LABELS } from '../../service/coaching'

// Admin rating of one coaching statement. Each save appends a new, attributable record.
export default function CoachingRating ({ uploadId, jobId, statementId, revision, current, onSaved }) {
  const [rating, setRating] = useState(current?.rating || '')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const requestId = useRef(null)
  useEffect(() => { setRating(current?.rating || ''); requestId.current = null }, [current?.id])
  useEffect(() => { requestId.current = null }, [rating, note])
  const save = async () => {
    setBusy(true); setError('')
    requestId.current ||= crypto.randomUUID()
    try {
      const result = await rateCoaching(uploadId, jobId, { request_id: requestId.current, statement_id: statementId, rating, note: note.trim(), coaching_revision: revision })
      requestId.current = null; setNote('')
      onSaved(result.feedback)
    } catch (e) {
      setError(String(e.message || '').includes('409') ? 'This coaching changed. Reload the report before rating it.' : 'Rating was not saved. Retry to send the same rating again.')
    } finally { setBusy(false) }
  }
  return <Stack spacing={0.75} role="group" aria-label={`Rate statement ${statementId}`} sx={{ mt: 0.5 }}>
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 13 }}>Rate this coaching (admin beta):</Typography>
      <ToggleButtonGroup size="small" exclusive value={rating} disabled={busy} onChange={(_, value) => { if (value) setRating(value) }}>
        {Object.entries(RATING_LABELS).map(([value, label]) => <ToggleButton key={value} value={value}>{label}</ToggleButton>)}
      </ToggleButtonGroup>
      {current && <Typography variant="caption" sx={{ fontSize: 13 }}>Saved: {RATING_LABELS[current.rating]}</Typography>}
    </Stack>
    {rating && <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
      <TextField size="small" label="Note (optional)" value={note} disabled={busy} inputProps={{ maxLength: 2000 }} onChange={e => setNote(e.target.value)} sx={{ flex: 1 }} />
      <Button size="small" variant="outlined" disabled={busy || (current?.rating === rating && !note.trim())} onClick={save}>{busy ? 'Saving…' : 'Save rating'}</Button>
    </Stack>}
    {error && <Alert severity="error">{error}</Alert>}
  </Stack>
}
CoachingRating.propTypes = {
  uploadId: PropTypes.string.isRequired,
  jobId: PropTypes.string.isRequired,
  statementId: PropTypes.string.isRequired,
  revision: PropTypes.string.isRequired,
  current: PropTypes.object,
  onSaved: PropTypes.func.isRequired
}
